export const PAGE_SIZE = 12;

export function parsePriceToNumber(valor) {
  if (typeof valor !== "string" || !valor.trim()) return NaN;
  const digits = valor.replace(/[^\d]/g, "");
  if (!digits) return NaN;
  return parseFloat(digits) / 100;
}

export function normalizeText(value) {
  return (value ?? "").toString().trim().toLowerCase();
}

function dedupeLabels(values) {
  const map = new Map();
  values.forEach((raw) => {
    const label = (raw ?? "").toString().trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (!map.has(key)) map.set(key, label);
  });
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

export function getCityOptions(properties) {
  return dedupeLabels(properties.map((p) => p.imovelInfo?.cidade));
}

export function getNeighborhoodOptions(properties, city) {
  const scoped = city
    ? properties.filter(
        (p) => normalizeText(p.imovelInfo?.cidade) === normalizeText(city)
      )
    : properties;
  return dedupeLabels(scoped.map((p) => p.imovelInfo?.bairro));
}

export function sortProperties(list, sortKey) {
  if (sortKey !== "menor-preco" && sortKey !== "maior-preco") {
    return list;
  }

  const withPrice = [];
  const withoutPrice = [];

  list.forEach((item) => {
    const price = parsePriceToNumber(item.informacoesComerciais?.valor);
    if (Number.isFinite(price)) {
      withPrice.push({ item, price });
    } else {
      withoutPrice.push(item);
    }
  });

  withPrice.sort((a, b) =>
    sortKey === "menor-preco" ? a.price - b.price : b.price - a.price
  );

  return [...withPrice.map((entry) => entry.item), ...withoutPrice];
}

/**
 * Reaplica sobre `list` os critérios de uma pesquisa real feita no
 * FilterModal (categoria, cidade, bairro, quartos/banheiros/vagas,
 * comodidades, código) — usando a mesma semântica de comparação do
 * FilterModal (trim + lowercase, "algum dos selecionados", etc). Modalidade
 * nunca é reaplicada aqui: quem decide a modalidade é o controle de
 * finalidade da própria página (Todos/Comprar/Alugar).
 *
 * A faixa de preço só é reaplicada quando `currentModality` é exatamente a
 * modalidade que estava ativa no momento da pesquisa — a escala de preço de
 * Venda e Aluguel são incomparáveis, então herdar um teto de "até R$
 * 500.000" ao trocar para Alugar produziria uma lista vazia sem sentido em
 * vez de simplesmente não aplicar aquele critério.
 */
export function applySearchCriteria(list, criteria, currentModality) {
  if (!criteria) return list;

  let out = list;

  if (criteria.categorias?.length) {
    out = out.filter((imovel) =>
      criteria.categorias.some((cat) =>
        normalizeText(imovel.imovelInfo?.tipo).includes(normalizeText(cat))
      )
    );
  }

  if (criteria.cidades?.length) {
    out = out.filter((imovel) =>
      criteria.cidades.some(
        (cidade) =>
          normalizeText(imovel.imovelInfo?.cidade) === normalizeText(cidade)
      )
    );
  }

  if (criteria.bairros?.length) {
    out = out.filter((imovel) =>
      criteria.bairros.some(
        (bairro) =>
          normalizeText(imovel.imovelInfo?.bairro) === normalizeText(bairro)
      )
    );
  }

  if (criteria.quartos && criteria.quartos !== "Qualquer") {
    out = out.filter(
      (imovel) => (imovel.caracteristicas?.quartos || 0) >= Number(criteria.quartos)
    );
  }

  if (criteria.banheiros && criteria.banheiros !== "Qualquer") {
    out = out.filter(
      (imovel) =>
        (imovel.caracteristicas?.banheiros || 0) >= Number(criteria.banheiros)
    );
  }

  if (criteria.vagas && criteria.vagas !== "Qualquer") {
    out = out.filter(
      (imovel) => (imovel.caracteristicas?.vagas || 0) >= Number(criteria.vagas)
    );
  }

  if (criteria.comodidades?.length) {
    out = out.filter((imovel) => {
      if (!imovel.selectedItems) return false;
      const comodidadesDoImovel = Object.values(imovel.selectedItems).map((i) =>
        normalizeText(i)
      );
      return criteria.comodidades.some((c) =>
        comodidadesDoImovel.includes(normalizeText(c))
      );
    });
  }

  if (criteria.codigo && criteria.codigo.trim()) {
    const search = normalizeText(criteria.codigo);
    out = out.filter(
      (imovel) => normalizeText(imovel.imovelInfo?.codigo) === search
    );
  }

  if (
    currentModality === criteria.modality &&
    (Number.isFinite(criteria.precoMin) || Number.isFinite(criteria.precoMax))
  ) {
    // Mesmo teto usado pelo FilterModal (handleModeChange): quando o
    // usuário não mexe no slider, precoMax nasce igual a esse valor. Nesse
    // caso o FilterModal trata como "sem limite superior" (mostra também
    // imóveis acima do teto) — precisamos replicar essa exceção aqui, senão
    // um imóvel acima do teto default aparece no contador do modal mas
    // some da lista de /pesquisa.
    const limiteMaximo = criteria.modality === "alugar" ? 10000 : 10000000;
    const precoMaxIlimitado =
      Number.isFinite(criteria.precoMax) && criteria.precoMax >= limiteMaximo;

    out = out.filter((imovel) => {
      const preco = parsePriceToNumber(imovel.informacoesComerciais?.valor);
      if (!Number.isFinite(preco)) return false;
      if (Number.isFinite(criteria.precoMin) && preco < criteria.precoMin) {
        return false;
      }
      if (
        !precoMaxIlimitado &&
        Number.isFinite(criteria.precoMax) &&
        preco > criteria.precoMax
      ) {
        return false;
      }
      return true;
    });
  }

  return out;
}
